import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Music, Shield, Users, Zap, CheckCircle, ArrowRight, Headphones, Radio, BarChart3, Globe } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STOCK_IMAGES = {
  studio1: "https://images.pexels.com/photos/5749200/pexels-photo-5749200.jpeg?auto=compress&cs=tinysrgb&w=800",
  studio2: "https://images.pexels.com/photos/5749189/pexels-photo-5749189.jpeg?auto=compress&cs=tinysrgb&w=800",
  studio3: "https://images.unsplash.com/photo-1763771757355-d2a395b5f8ea?w=800&fit=crop",
};

const AboutPage = () => {
  const { t } = useLanguage();
  const [aboutData, setAboutData] = useState(null);
  const [loading, setLoading] = useState(true);

  const ap = t('aboutPage') || {};
  const offers = ap.offers || [];
  const teamLabels = ap.teamLabels || [];
  const howWeWorkItems = ap.howWeWork || [];
  const trustElements = ap.trustElements || [];
  const offerIcons = [Music, Shield, Users, Zap];
  const howIcons = [Headphones, Shield, Radio, BarChart3];

  useEffect(() => {
    const fetchData = async () => {
      try {
        const aboutRes = await axios.get(`${API_URL}/api/content/about`).catch(() => ({ data: { content: {} } }));
        if (aboutRes.data?.content && Object.keys(aboutRes.data.content).length > 0) setAboutData(aboutRes.data.content);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* HERO */}
      <section className="section-padding luxury-mesh">
        <div className="container-custom text-center">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E8720C] mb-4">{ap.overline}</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6" data-testid="about-title">
            {ap.title}
          </h1>
          <p className="text-base md:text-lg text-white/50 max-w-3xl mx-auto leading-relaxed">
            {aboutData?.intro || ap.introDefault}
          </p>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{ap.whatWeOffer}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {offers.map((item, i) => {
              const Icon = offerIcons[i] || Music;
              return (
                <div key={i} className="glass-card p-8 text-center hover:-translate-y-1 transition-transform">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E8720C]/20 to-[#E8720C]/5 flex items-center justify-center mx-auto mb-5">
                    <Icon className="w-6 h-6 text-[#E8720C]" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                  <p className="text-sm text-white/50">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* VISION */}
      <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent">
        <div className="container-custom">
          <div className="glass-card p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/5 via-transparent to-transparent" />
            <div className="relative max-w-3xl mx-auto text-center">
              <Globe className="w-12 h-12 text-[#E8720C] mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-6">{ap.visionTitle}</h2>
              <p className="text-lg text-white/60 leading-relaxed" data-testid="about-vision">
                {aboutData?.vision || ap.visionDefault}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM & WORKFLOW */}
      <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent" data-testid="about-team-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{ap.teamTitle}</h2>
            <p className="text-white/50 max-w-2xl mx-auto">{aboutData?.team_description || ap.teamDefault}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { img: aboutData?.team_image1 || STOCK_IMAGES.studio1, label: teamLabels[0] },
              { img: aboutData?.team_image2 || STOCK_IMAGES.studio2, label: teamLabels[1] },
              { img: aboutData?.team_image3 || STOCK_IMAGES.studio3, label: teamLabels[2] },
            ].map((item, i) => (
              <div key={i} className="glass-card p-2 group overflow-hidden">
                <div className="relative overflow-hidden rounded-xl">
                  <img src={item.img} alt={item.label} className="w-full aspect-[4/3] object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <p className="absolute bottom-4 left-4 text-sm font-semibold">{item.label}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW WE WORK */}
      <section className="section-padding" data-testid="about-how-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">{ap.howTitle}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {howWeWorkItems.map((item, i) => {
              const Icon = howIcons[i] || Music;
              return (
                <div key={i} className="glass-card p-8 flex gap-5 items-start hover:-translate-y-1 transition-transform">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#E8720C]/20 to-[#E8720C]/5 flex items-center justify-center shrink-0">
                    <Icon className="w-6 h-6 text-[#E8720C]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                    <p className="text-sm text-white/50 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* TRUST ELEMENTS */}
      <section className="section-padding bg-gradient-to-b from-transparent via-[#0a0a0c] to-transparent" data-testid="about-trust-section">
        <div className="container-custom">
          <div className="glass-card p-8 md:p-12 relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-[#E8720C]/5 via-transparent to-transparent blur-3xl" />
            <div className="relative text-center">
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8">{ap.trustTitle}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto mb-10">
                {trustElements.map((item, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-white/5">
                    <CheckCircle className="w-5 h-5 text-[#E8720C] shrink-0" />
                    <span className="text-sm text-white/70">{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/catalog" className="btn-gold" data-testid="about-explore-catalog">
                  {ap.exploreCatalog} <ArrowRight className="w-4 h-4 inline ml-1" />
                </Link>
                <Link to="/artists" className="btn-secondary" data-testid="about-view-artists">
                  {ap.viewArtists}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
